<template>
  <v-container fluid class="pa-6">
    <v-row>
      <v-col cols="12">
        <v-toolbar flat color="white" class="mb-6">
          <v-toolbar-title>My Firms & Applications</v-toolbar-title>
          <v-spacer></v-spacer>
          <v-btn
            outlined
            color="primary"
            @click="activationDrawer = true"
            class="elevation-0 mr-4"
          >
            <v-icon left>mdi-account-check</v-icon>
            Activate My Firm
          </v-btn>
          <v-btn color="primary" @click="createFirm" class="elevation-2">
            <v-icon left>mdi-plus</v-icon>
            New Firm Application
          </v-btn>
        </v-toolbar>
      </v-col>
    </v-row>
    <v-row>
      <v-col cols="12">
        <v-alert v-if="error" type="error" dense class="mb-4">{{
          error
        }}</v-alert>
        <v-skeleton-loader
          v-if="loading"
          type="table"
          :loading="loading"
          class="mb-4"
        />
        <v-card v-else class="elevation-2 overflow-hidden">
          <v-tabs
            v-model="activeTab"
            background-color="grey lighten-5"
            color="primary"
            class="px-4 pt-2"
          >
            <v-tab class="text-capitalize font-weight-bold">
              <v-icon left small>mdi-office-building</v-icon>
              Firm Profiles
            </v-tab>
            <v-tab class="text-capitalize font-weight-bold">
              <v-icon left small>mdi-file-document-edit</v-icon>
              Firm Applications
            </v-tab>
          </v-tabs>

          <v-divider></v-divider>

          <v-data-table
            :headers="headers"
            :items="displayFirms"
            item-key="id"
            class="elevation-0 mb-2 pa-4"
            :search="search"
            :loading="loading"
            :footer-props="{ itemsPerPageOptions: [5, 10, 20] }"
          >
            <template v-slot:top>
              <v-text-field
                v-model="search"
                append-icon="mdi-magnify"
                :label="searchPlaceholder"
                single-line
                hide-details
                outlined
                dense
                class="mb-6 mt-2 mx-1"
                @keydown="cancelPendingRequest"
              />
            </template>
            <template v-slot:item.status="{ item }">
              <v-chip
                :color="statusColor(item?.firmApplication?.status ?? '')"
                dark
                small
                class="font-weight-medium"
                >{{ item?.firmApplication?.status }}</v-chip
              >
            </template>
            <template v-slot:item.created_at="{ item }">
              {{ formatDate(item?.firmApplication?.applicationDate, 10) }}
            </template>
            <template v-slot:item.actions="{ item }">
              <div class="d-flex justify-center align-center flex-nowrap">
                <v-tooltip bottom>
                  <template v-slot:activator="{ on, attrs }">
                    <v-btn
                      small
                      depressed
                      color="primary"
                      @click="viewFirm(item)"
                      class="ma-1 text-capitalize px-4"
                      v-bind="attrs"
                      v-on="on"
                    >
                      <v-icon left small>mdi-eye</v-icon>
                      View details
                    </v-btn>
                  </template>
                  <span>See full information for this firm</span>
                </v-tooltip>

                <v-tooltip bottom v-if="canEdit(item)">
                  <template v-slot:activator="{ on, attrs }">
                    <v-btn
                      small
                      depressed
                      color="warning"
                      @click="editFirm(item)"
                      class="ma-1 text-capitalize px-4"
                      v-bind="attrs"
                      v-on="on"
                    >
                      <v-icon left small>mdi-pencil</v-icon>
                      Continue
                    </v-btn>
                  </template>
                  <span>Resume and complete this application</span>
                </v-tooltip>

                <v-tooltip bottom v-if="canDelete(item)">
                  <template v-slot:activator="{ on, attrs }">
                    <v-btn
                      small
                      outlined
                      color="error"
                      @click="deleteFirm(item)"
                      class="ma-1 text-capitalize px-4"
                      v-bind="attrs"
                      v-on="on"
                    >
                      <v-icon left small>mdi-delete</v-icon>
                      Remove
                    </v-btn>
                  </template>
                  <span>Permanently delete this draft</span>
                </v-tooltip>
              </div>
            </template>
          </v-data-table>
        </v-card>
      </v-col>
    </v-row>

    <!-- Details Drawer -->
    <firm-details-drawer v-model="detailsDrawer" />

    <!-- Activation Drawer -->
    <firm-activation-drawer v-model="activationDrawer" />
  </v-container>
</template>

<script>
import { AuthService } from "../../auth";
import DateMixin from "../../../mixins/DateMixin";
import FirmDetailsDrawer from "../components/FirmDetailsDrawer.vue";
import FirmActivationDrawer from "../components/FirmActivationDrawer.vue";

export default {
  name: "FirmProfile",
  mixins: [DateMixin],
  components: {
    FirmDetailsDrawer,
    FirmActivationDrawer,
  },
  data() {
    return {
      loading: true,
      error: "",
      search: "",
      activeTab: 0,
      activationDrawer: false,
      detailsDrawer: false,
      headers: [
        {
          text: "Firm Name",
          value: "firmName",
          class: "subtitle-1 font-weight-bold",
        },
        {
          text: "Your Position",
          value: "position",
          class: "subtitle-1 font-weight-bold",
        },
        {
          text: "Status",
          value: "status",
          class: "subtitle-1 font-weight-bold",
        },
        {
          text: "Date",
          value: "created_at",
          class: "subtitle-1 font-weight-bold",
        },
        {
          text: "Available Actions",
          value: "actions",
          sortable: false,
          align: "center",
          class: "subtitle-1 font-weight-bold",
        },
      ],
    };
  },
  mounted() {
    this.fetchFirms();
    const isRedirection = JSON.parse(
      sessionStorage.getItem("after_login_redirect") || "null"
    );
    if (isRedirection) {
      sessionStorage.removeItem("after_login_redirect");
    }

    Event.$on("toggle-firms-list-loading", (value) => {
      this.loading = value;
    });
  },
  computed: {
    firms() {
      return this.$store.getters["Firms/firms"] || [];
    },
    displayFirms() {
      if (this.activeTab === 0) {
        // Firm Profiles - only show Active/Registered firms
        return this.firms.filter(
          (f) =>
            f.firmApplication?.status === "Active" ||
            f.firmApplication?.status === "Registered"
        );
      } else {
        // Firm Applications - show ongoing processes
        return this.firms.filter(
          (f) =>
            f.firmApplication?.status !== "Active" &&
            f.firmApplication?.status !== "Registered"
        );
      }
    },
    searchPlaceholder() {
      return this.activeTab === 0
        ? "Search active firm profiles by name, registration number, or email..."
        : "Search ongoing applications by name, number, or status...";
    },
    user() {
      return AuthService.user;
    },
  },
  watch: {
    search: {
      handler() {
        this.debouncedSearch();
      },
    },
    detailsDrawer(val) {
      if (!val) {
        this.$store.commit("Firms/SET_SELECTED_FIRM_DETAILS", null);
      }
    },
    activeTab: {
      handler(newTab, oldTab) {
        if (newTab !== oldTab) {
          this.fetchFirms();
        }
      },
    },
  },
  methods: {
    cancelPendingRequest() {
      if (this.abortSignal) {
        this.abortSignal?.abort("New request initiated");
      }
    },
    debouncedSearch() {
      const q = {
        term: btoa(this.search.trim()),
        limit: 10,
        select: true,
      };
      setTimeout(() => {
        if (this.search) this.$store.dispatch("Search/search", q);
      }, 500);
    },
    async fetchFirms() {
      try {
        this.activeTab === 0
          ? this.$store.dispatch("Firms/getFirmProfiles", {
              profileId: this.user.profile_id,
            })
          : this.$store.dispatch("Firms/getFirmApplications", {
              profileId: this.user.profile_id,
            });
      } catch (e) {
        console.error(e);
        this.error = "Failed to load firms.";
        this.loading = false;
      }
    },
    statusColor(status) {
      switch (status) {
        case "Active":
        case "Registered":
          return "success";
        case "In Review":
          return "info";
        case "Draft":
        case "New":
          return "warning";
        case "Rejected":
          return "error";
        default:
          return "grey darken-1";
      }
    },
    viewFirm(item) {
      const profileID =
        item.profileID ||
        item.firmApplication?.profileID ||
        item.userProfiles?.ProfileID;
      if (profileID) {
        this.$store.dispatch("Firms/getFirmFullDetails", profileID);
        this.detailsDrawer = true;
      }
    },
    editFirm(item) {
      if (!item || !item.firmApplication) {
        this.error =
          "You are not able to edit this firm at this time. Please try again later.";
        return;
      }
      // Route to edit form
      this.$router.push({
        name: "firm-card",
        params: {
          firmCode: item?.firmApplication?.profileID,
          application: item?.firmApplication?.code,
        },
      });
    },
    deleteFirm(item) {
      if (
        confirm(
          `Are you sure you want to permanently remove ${item.firmName}? This action cannot be undone.`
        )
      ) {
        // Implement delete logic if available in store
      }
    },
    createFirm() {
      this.$router.push({ name: "firm-card" });
    },
    canEdit(item) {
      const isCreator =
        this.user.profile_id === item?.firmApplication?.createdByProfileID;
      return (
        (item?.firmApplication?.status === "New" ||
          item?.firmApplication?.status === "Draft") &&
        isCreator
      );
    },
    canDelete(item) {
      const isCreator =
        this.user.profile_id === item?.firmApplication?.createdByProfileID;
      return (
        (item?.firmApplication?.status === "New" ||
          item?.firmApplication?.status === "Draft") &&
        isCreator
      );
    },
  },
};
</script>

<style scoped>
.v-toolbar {
  margin-bottom: 24px;
}

.v-data-table {
  margin-top: 8px;
}

.v-card {
  border-radius: 12px;
}

.v-container {
  min-height: 80vh;
}

.bg-light {
  background-color: #f8f9fa;
}
</style>

<style scoped>
.v-toolbar {
  margin-bottom: 24px;
}

.v-data-table {
  margin-top: 8px;
}

.v-card {
  border-radius: 12px;
}

.v-container {
  min-height: 80vh;
}

.bg-light {
  background-color: #f8f9fa;
}
</style>
